import React, { useRef } from 'react'
import { Tooltip } from 'antd'
import IntlMessages from '../../util/IntlMessages'
import LinkButton from '../link-button'
import { track } from '../../services/analytics/analytics'
import { getAnalyticsProperties } from '../../util/analytics-helpers'

interface FreemiumPricingSimpleTooltipProps {
  children: React.ReactElement
  isFreemiumPlan: boolean
  onCorePlan?: boolean
  featureName?: string
  featureCategory?: string
}

const FreemiumPricingSimpleTooltip: React.FC<FreemiumPricingSimpleTooltipProps> = ({ 
  children,
  isFreemiumPlan,
  onCorePlan = true,
  featureName,
  featureCategory,
}) => {
  const hasTrackedView = useRef(false)

  if (!isFreemiumPlan) {
    return children
  }

  const handleTooltipVisibilityChange = (visible: boolean) => {
    if (visible && !hasTrackedView.current && featureName && featureCategory) {
      hasTrackedView.current = true
      track('FEATURE_GATE_VIEWED', {
        ...getAnalyticsProperties(),
        featureName,
        featureCategory,
        pagePath: window.location.pathname,
      })
    }
  }

  const handleUpgradeClick = () => {
    if (featureName && featureCategory) {
      track('UPGRADE_CTA_CLICKED', {
        ...getAnalyticsProperties(),
        featureName,
        featureCategory,
        pagePath: window.location.pathname,
      })
    }
  }

  const tooltipContent = (
    <div className='freemium-pricing-simple-tooltip-content'>
      <div className='title'>
        <IntlMessages 
          id="components.freemiumPricingTooltip.freemiumPlanSimpleInfo" 
          values={{
            onCorePlan: (chunk) => onCorePlan ? chunk : null,
          }}
        />
      </div>
      <div className='footer'>
        <LinkButton to={'/app/settings/billing'} block type="primary" size='large' onClick={handleUpgradeClick}>
          <IntlMessages id="components.freemiumPricingTooltip.upgradePlan" />
        </LinkButton>
      </div>
    </div>
  )

  return (
    <Tooltip
      placement="bottom"
      classNames={{ 
        root: 'freemium-pricing-simple-tooltip',
      }}
      title={tooltipContent}
      onOpenChange={handleTooltipVisibilityChange}
    >
      {/* 
        Span wrapper is required because Antd Tooltip passes mouse event handlers (onMouseEnter, onMouseLeave) 
        to its child element. When the child is a complex component (e.g. Card wrapped in Link/div), these event 
        handlers don't get properly forwarded through that structure. The span accepts those handlers and allows 
        the tooltip to work correctly.
      */}
      <span>
        {children}
      </span>
    </Tooltip>
  )
}

export default FreemiumPricingSimpleTooltip
